import React, { useEffect, useRef, useState } from "react";
import { apiFetch } from "../../api/client";
import { useUpdateAthlete } from "../../hooks/queries/useAthletes";
import { useAuth } from "../../context/AuthContext";

export default function GuardianPendingPage({ advisory = false }) {
  const { guardianVerification, signOut, athleteData } = useAuth();
  const updateAthlete = useUpdateAthlete();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(athleteData?.guardian_name || athleteData?.attributes?.guardian_name || "");
  const [email, setEmail] = useState(athleteData?.guardian_email || athleteData?.attributes?.guardian_email || "");
  const cooldownKey = `athletain.guardian-resend.${athleteData?.id || "current"}`;
  const [remaining, setRemaining] = useState(() => {
    try { return Math.max(0, Math.min(60, Math.ceil((Number(localStorage.getItem(cooldownKey)) - Date.now()) / 1000))) || 0; }
    catch { return 0; }
  });
  const cooldownUntil = useRef(Date.now() + remaining * 1000);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const busy = useRef(false);
  useEffect(() => {
    if (!remaining) return;
    const timer = setTimeout(() => setRemaining(Math.max(0, Math.ceil((cooldownUntil.current - Date.now()) / 1000))), 1000);
    return () => clearTimeout(timer);
  }, [remaining]);
  const resend = async () => {
    if (busy.current || remaining) return;
    busy.current = true;
    setSending(true);
    try {
      await apiFetch("/guardian/verification/resend", { method: "POST" });
      cooldownUntil.current = Date.now() + 60_000;
      setRemaining(60);
      try { localStorage.setItem(cooldownKey, String(Date.now() + 60_000)); } catch { /* Server also enforces cooldown. */ }
      setMessage("Solicitud recibida. Si corresponde, enviaremos un enlace al correo de tu tutor. Revisa también la carpeta de correo no deseado.");
    } catch { setMessage("No pudimos solicitar el correo. Inténtalo más tarde."); }
    finally { busy.current = false; setSending(false); }
  };
  const saveContact = async event => {
    event.preventDefault();
    if (busy.current || !athleteData?.id) return;
    busy.current = true;
    try {
      await updateAthlete.mutateAsync({ id: athleteData.id, guardian_name: name.trim(), guardian_email: email.trim() });
      setEditing(false);
      setMessage("Contacto actualizado. Solicita un nuevo correo; el enlace anterior dejará de ser válido.");
    } catch { setMessage("No pudimos actualizar el contacto. Revisa los datos e inténtalo de nuevo."); }
    finally { busy.current = false; }
  };
  const content = <section className="w-full max-w-lg space-y-5 rounded-xl bg-white p-6 text-gray-700">
    <h1 className="text-2xl font-bold text-blue-950">Verificación de tu tutor pendiente</h1>
    <p>Tu padre, madre o tutor legal debe abrir el correo de Athletain, verificar su identidad con su cédula y fecha de expedición, y autorizar tu uso de la aplicación.</p>
    <p>{advisory ? "Puedes continuar usando tu cuenta mientras completas este paso." : "Tu cuenta está creada. Podrás continuar automáticamente cuando tu tutor complete la verificación."}</p>
    {guardianVerification?.state === "locked" && <p role="status">Se alcanzó el límite de intentos. {guardianVerification.retry_at ? `Solicita otro enlace después del ${new Date(guardianVerification.retry_at).toLocaleString("es-CO")}.` : "Inténtalo más tarde."}</p>}
    <button type="button" onClick={resend} disabled={sending || remaining > 0 || guardianVerification?.state === "locked"} className="min-h-12 rounded-lg bg-blue-950 px-5 py-3 text-white disabled:opacity-50">{sending ? "Solicitando…" : remaining ? `Solicitar otro correo en ${remaining} s` : "Solicitar correo al tutor"}</button>
    <button type="button" onClick={() => setEditing(value => !value)} className="min-h-11 text-blue-950 underline">Corregir contacto del tutor</button>
    {editing && <form onSubmit={saveContact} className="space-y-4">
      <label className="block">Nombre completo del tutor<input required minLength={3} value={name} onChange={event => setName(event.target.value)} className="mt-1 w-full rounded-lg border p-3 text-base" /></label>
      <label className="block">Correo del tutor<input type="email" required value={email} onChange={event => setEmail(event.target.value)} className="mt-1 w-full rounded-lg border p-3 text-base" /></label>
      <button disabled={updateAthlete.isPending} className="min-h-11 underline text-blue-950" type="submit">Guardar contacto</button>
    </form>}
    <p role="status" aria-live="polite">{message}</p>
    {!advisory && <button type="button" onClick={signOut} className="min-h-11 text-blue-950 underline">Cerrar sesión</button>}
  </section>;
  return advisory ? content : <main className="flex min-h-screen items-center justify-center bg-gray-50 px-4 py-8">{content}</main>;
}
